import axios from 'axios';
import { baseURL } from '../Constants';
import { PlayerStats } from '../models/PlayerStats';
import { PlayerStatsAPI } from '../models/PlayerStatsAPI';
import { LeaderboardPlayer } from '../models/LeaderboardPlayer';
import { LeaderboardGame } from '../models/LeaderboardGame';
import { SeasonPlayerRanking } from '../types/types';
import forEach from 'lodash/forEach';

const url = `${baseURL}/PlayerStat`;

const roundPlayerStats = (player: PlayerStats): PlayerStats => {
    const roundedPlayer: PlayerStats = {
        ...player,
        kills: Math.round(player.kills * 100) / 100,
        hsKills: Math.round(player.hsKills * 100) / 100,
        deaths: Math.round(player.deaths * 100) / 100,
        assists: Math.round(player.assists * 100) / 100,
        hs: Math.round(player.hs * 100) / 100,
        dmg: Math.round(player.dmg * 100) / 100,
        adr: Math.round(player.adr * 100) / 100,
        kd: Math.round(player.kd * 100) / 100,
        fk: Math.round(player.fk * 100) / 100,
        kast: Math.round(player.kast * 100) / 100,
        rating1: Math.round(player.rating1 * 100) / 100,
        rating2: Math.round(player.rating2 * 100) / 100,
        rating21: Math.round(player.rating21 * 100) / 100,
        openingDuelWinPercentage: Math.round(player.openingDuelWinPercentage * 100) / 100,
        openingDuelAttemptPercentage: Math.round(player.openingDuelAttemptPercentage * 100) / 100,
        enemyFlashTime: Math.round(player.enemyFlashTime * 100) / 100,
        timeFlashed: Math.round(player.timeFlashed * 100) / 100,
        utilityDamage: Math.round(player.utilityDamage * 100) / 100,
        gamesPlayed: Math.round(player.gamesPlayed * 100) / 100,
    };
    return roundedPlayer;
}


const roundPlayersStats = (playersData: PlayerStats[]): PlayerStats[] => {
    if (playersData === undefined) {
        return [];
    }
    const roundedPlayers = playersData.map((player: PlayerStats) => {
        return roundPlayerStats(player);
    });

    return roundedPlayers;
}

export const fetchTop10Players = async (
    season: number, 
    steamId: string | null, 
    division: string | null, 
    group: string | null, 
    side?: 'T' | 'CT'
): Promise<{ [stat: string]: LeaderboardPlayer[] }> => {
    try {
        let completeUrl = `${url}/leaderboard?season=${season}`
        if (steamId !== null) {
            completeUrl += `&steamId=${steamId}`
        }
        if (division !== null) {
            completeUrl += `&division=${division}`
        }
        if (group !== null) {
            completeUrl += `&group=${group}`
        }
        if (side !== undefined) {
            completeUrl += `&side=${side}`
        }
        const response = await axios.get(completeUrl);
        const leaderboardPlayers = response.data as { [stat: string]: LeaderboardPlayer[] };

        // round every leaderboard player stats
        forEach(leaderboardPlayers, (players: LeaderboardPlayer[]) => {
            players.forEach((player: LeaderboardPlayer) => {
                player.stats = roundPlayerStats(player.stats);
            });
        });


        return leaderboardPlayers;
    }
    catch (error) {
        throw error;
    }
}

/** The best single-map lines per stat — the leaderboard's "Games" view. */
export const fetchTopGames = async (
    season: number,
    steamId: string | null,
    division: string | null,
    group: string | null
): Promise<{ [stat: string]: LeaderboardGame[] }> => {
    const params: Record<string, string | number> = { season };
    if (steamId !== null) params.steamId = steamId;
    if (division !== null) params.division = division;
    if (group !== null) params.group = group;
    const response = await axios.get(`${url}/leaderboard/games`, { params });
    return response.data as { [stat: string]: LeaderboardGame[] };
}

export const fetchPlayersByDivision = async (division: string): Promise<PlayerStats[]> => {
    try {
        const response = await axios.get(`${url}/playersByDivision/${division}`);
        return roundPlayersStats(response.data);
    }
    catch (error) {
        throw error;
    }
}

export const fetchSeasonsPlayed = async (steamId: string): Promise<number[]> => {
    try {
        const response = await axios.get(`${url}/getSeasonsPlayed/${steamId}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export type SeasonDivisionCount = { division: string; maps: number };

// Maps per division for one season (drives the Statistik tab's liga/kval picker).
export const fetchSeasonDivisions = async (steamId: string, season: number): Promise<SeasonDivisionCount[]> => {
    const response = await axios.get(`${url}/getSeasonDivisions`, { params: { steamId, season } });
    return response.data;
}

export const fetchAllSeasonPlayerRankings = async (steamId: string): Promise<SeasonPlayerRanking[]> => {
    try {
        const response = await axios.get(`${baseURL}/PlayerStat/player-all-season-rankings`, {
            params: { steamId }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to fetch all season player rankings:', error);
        throw error;
    }
}

export const fetchPlayerBySteamIdAndSeason = async (steamId: string, season: number, scope?: 'liga' | 'kval'): Promise<PlayerStatsAPI | undefined> => {
    try {
        const response = await axios.get(`${url}/getPlayerBySeasonAndSteamId`, {
            params: {
                steamId,
                season,
                scope
            }
        });

        const data = response.data as PlayerStatsAPI;
        // Round the stats while preserving the API's property names
        if (data) {
            // Round overall stats
            data.rating2 = Math.round(data.rating2 * 100) / 100;
            data.rating21 = Math.round(data.rating21 * 100) / 100;
            data.hs = Math.round(data.hs * 100) / 100;
            data.kd = Math.round(data.kd * 100) / 100;
            data.adr = Math.round(data.adr * 100) / 100;
            data.kast = Math.round(data.kast * 100) / 100;
            data.openingDuelWinPercentage = Math.round(data.openingDuelWinPercentage * 100) / 100;
            data.openingDuelAttemptPercentage = Math.round(data.openingDuelAttemptPercentage * 100) / 100;
            data.enemyFlashTime = Math.round(data.enemyFlashTime * 100) / 100;
            data.timeFlashed = Math.round(data.timeFlashed * 100) / 100;
            data.utilityDamage = Math.round(data.utilityDamage * 100) / 100;

            // Round Rating 3.0 fields — explicit null checks so null passes through
            // untouched (null = no R3 data; must never be coerced to 0)
            if (data.rating30 != null) data.rating30 = Math.round(data.rating30 * 100) / 100;
            if (data.swingPct != null) data.swingPct = Math.round(data.swingPct * 100) / 100;
            if (data.t_Rating30 != null) data.t_Rating30 = Math.round(data.t_Rating30 * 100) / 100;
            if (data.t_SwingPct != null) data.t_SwingPct = Math.round(data.t_SwingPct * 100) / 100;
            if (data.cT_Rating30 != null) data.cT_Rating30 = Math.round(data.cT_Rating30 * 100) / 100;
            if (data.cT_SwingPct != null) data.cT_SwingPct = Math.round(data.cT_SwingPct * 100) / 100;

            // Round T-side stats with alternative casing
            if (data.t_Rating2) data.t_Rating2 = Math.round(data.t_Rating2 * 100) / 100;
            if (data.t_Rating21) data.t_Rating21 = Math.round(data.t_Rating21 * 100) / 100;
            if (data.t_HS) data.t_HS = Math.round(data.t_HS * 100) / 100;
            if (data.t_KD) data.t_KD = Math.round(data.t_KD * 100) / 100;
            if (data.t_ADR) data.t_ADR = Math.round(data.t_ADR * 100) / 100;
            if (data.t_KAST) data.t_KAST = Math.round(data.t_KAST * 100) / 100;
            if (data.t_openingDuelWinPercentage) data.t_openingDuelWinPercentage = Math.round(data.t_openingDuelWinPercentage * 100) / 100;
            if (data.t_openingDuelAttemptPercentage) data.t_openingDuelAttemptPercentage = Math.round(data.t_openingDuelAttemptPercentage * 100) / 100;
            if (data.t_enemyFlashTime) data.t_enemyFlashTime = Math.round(data.t_enemyFlashTime * 100) / 100;
            if (data.t_timeFlashed) data.t_timeFlashed = Math.round(data.t_timeFlashed * 100) / 100;
            if (data.t_utilityDamage) data.t_utilityDamage = Math.round(data.t_utilityDamage * 100) / 100;
            
            // Round CT-side stats with alternative casing
            if (data.cT_Rating2) data.cT_Rating2 = Math.round(data.cT_Rating2 * 100) / 100;
            if (data.cT_Rating21) data.cT_Rating21 = Math.round(data.cT_Rating21 * 100) / 100;
            if (data.cT_HS) data.cT_HS = Math.round(data.cT_HS * 100) / 100;
            if (data.cT_KD) data.cT_KD = Math.round(data.cT_KD * 100) / 100;
            if (data.cT_ADR) data.cT_ADR = Math.round(data.cT_ADR * 100) / 100;
            if (data.cT_KAST) data.cT_KAST = Math.round(data.cT_KAST * 100) / 100;
            if (data.cT_openingDuelWinPercentage) data.cT_openingDuelWinPercentage = Math.round(data.cT_openingDuelWinPercentage * 100) / 100;
            if (data.cT_openingDuelAttemptPercentage) data.cT_openingDuelAttemptPercentage = Math.round(data.cT_openingDuelAttemptPercentage * 100) / 100;
            if (data.cT_enemyFlashTime) data.cT_enemyFlashTime = Math.round(data.cT_enemyFlashTime * 100) / 100;
            if (data.cT_timeFlashed) data.cT_timeFlashed = Math.round(data.cT_timeFlashed * 100) / 100;
            if (data.cT_utilityDamage) data.cT_utilityDamage = Math.round(data.cT_utilityDamage * 100) / 100;
        }
        
        return data;
    }
    catch (error) {
        throw error;
    }
}

// division (an exact match division, season 26+) returns only that division's maps and only the
// players who played there.
export const fetchPlayersByTeamId = async (teamId: string, season: number, division?: string): Promise<PlayerStats[]> => {
    try {
        const response = await axios.get(`${url}/getPlayersByTeamId`, { params: { teamid: teamId, season, division } });
        return roundPlayersStats(response.data);
    }
    catch (error) {
        throw error;
    }
}